/**
 * The two column dialogs the column header menu **cannot** own (P-080, P-087).
 *
 * `Menu` closes the menu for every row that has no submenu — `entry.onSelect(); onClose()` —
 * and `onClose` is `actions.openMenu(null)`, which unmounts `ColumnMenu`. Anything that row
 * put in `ColumnMenu`'s own `useState` therefore died in the same tick, which is exactly why
 * `Rename` opened nothing at all. Both dialogs are keyed off the store (`renamingColumnId`,
 * `deletingColumnId`) and rendered by `TableView`, the way `MoveItemsDialog` already was
 * (P-054). Any future menu row that opens a dialog belongs here too.
 */
import type { ColumnId } from '@elizmonday/shared';
import { useConfirm, useOverlayDismiss } from '@elizmonday/ui';
import { useEffect, useRef } from 'react';
import { useTable, useTableActions } from '../context';

/** P-080: monday renames a column from its own menu; this is that dialog. */
export function ColumnRenameDialog({ columnId }: { columnId: ColumnId }) {
  const payload = useTable((state) => state.payload);
  const actions = useTableActions();
  const column = payload?.columns.find((entry) => entry.id === columnId);
  const close = () => actions.setRenamingColumn(null);
  /**
   * **P-260.** The hoisted-dialog pattern this file was written for is exactly the one that lost
   * focus: the column menu unmounts in the commit that mounts this. The hook is called before
   * the early return below, so its position in the hook order never moves.
   */
  useOverlayDismiss({ onClose: close });
  if (!column) return null;

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={close}>
      <form
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Rename ${column.title}`}
        data-testid="column-rename-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          const input = event.currentTarget.elements.namedItem('title');
          const next = input instanceof HTMLInputElement ? input.value.trim() : '';
          close();
          if (next && next !== column.title) void actions.renameColumn(columnId, next);
        }}
      >
        <h2 className="ez-board-dialog__title">Rename column</h2>
        <label className="ez-board-dialog__field">
          Column name
          <input name="title" defaultValue={column.title} maxLength={255} autoFocus />
        </label>
        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={close}>
            Cancel
          </button>
          <button type="submit" className="ez-board-dialog__save" data-testid="column-rename-submit">
            Save
          </button>
        </footer>
      </form>
    </div>
  );
}

/**
 * P-087: the confirm in front of `Delete column`.
 *
 * It renders nothing of its own — the shell's `confirm()` (`data-testid="confirm-dialog"`) is
 * the one destructive dialog in this product (P-012) and the store cannot call a React context
 * — so this component exists only to bridge the two. monday names the column and says how many
 * rows lose a value; the count is the board's loaded row count, which is what the viewer can
 * see, and the wording says so rather than implying the whole board was counted.
 */
export function ColumnDeleteConfirm({ columnId }: { columnId: ColumnId }) {
  const payload = useTable((state) => state.payload);
  const actions = useTableActions();
  const confirm = useConfirm();
  const column = payload?.columns.find((entry) => entry.id === columnId);
  const title = column?.title ?? 'this column';
  const withValues = (payload?.groups ?? [])
    .flatMap((group) => group.page.items)
    .filter((row) => {
      const value = row.values[String(columnId)];
      return value !== null && value !== undefined && value !== '';
    }).length;
  /** StrictMode double-mounts in dev; one mount must raise exactly one dialog. */
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    void (async () => {
      const ok = await confirm({
        title: `Delete "${title}"?`,
        body:
          withValues > 0
            ? `${withValues} ${withValues === 1 ? 'item loses' : 'items lose'} the value in this column. You can undo this.`
            : 'The column and its values are removed from every item. You can undo this.',
        confirmLabel: 'Delete',
        danger: true,
      });
      if (!ok) {
        actions.setDeletingColumn(null);
        return;
      }
      await actions.deleteColumn(columnId);
    })();
    // The dialog is raised once per open; `columnId` identifies which open this is.
  }, [columnId, confirm, actions, title, withValues]);

  return null;
}
